
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { chatSettings } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Database } from '@/types/supabase';

type ChatSettings = Database['public']['Tables']['chat_settings']['Row'];

const ChatConfig = () => {
  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await chatSettings()
        .select()
        .single();

      if (error) {
        throw error;
      }

      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const { error } = await chatSettings()
        .update({
          openai_api_key: settings.openai_api_key,
          elevenlabs_api_key: settings.elevenlabs_api_key,
          voice_id: settings.voice_id,
          system_prompt: settings.system_prompt,
          enable_voice: settings.enable_voice,
          enable_whisper: settings.enable_whisper,
          updated_at: new Date().toISOString()
        })
        .eq('id', 1);

      if (error) {
        throw error;
      }

      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Chat Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center items-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Chat Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center p-4">
            <Button onClick={loadSettings}>Retry Loading Settings</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Chat Configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="openai_api_key">OpenAI API Key</Label>
          <Input
            id="openai_api_key"
            value={settings.openai_api_key || ''}
            onChange={(e) => setSettings({ ...settings, openai_api_key: e.target.value })}
            placeholder="Enter your OpenAI API key"
            type="password"
          />
          <p className="text-sm text-gray-500">Required for AI chat functionality</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="system_prompt">System Prompt</Label>
          <Textarea
            id="system_prompt"
            value={settings.system_prompt || ''}
            onChange={(e) => setSettings({ ...settings, system_prompt: e.target.value })}
            placeholder="Enter instructions for the AI assistant"
            rows={4}
          />
          <p className="text-sm text-gray-500">Instructions that guide how the AI responds</p>
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="enable_whisper">Enable Whisper (Speech to Text)</Label>
            <p className="text-sm text-gray-500">Allow users to speak their messages</p>
          </div>
          <Switch
            id="enable_whisper"
            checked={settings.enable_whisper}
            onCheckedChange={(checked) => setSettings({ ...settings, enable_whisper: checked })}
          />
        </div>

        <div className="space-y-4 pt-4 border-t">
          <h3 className="text-lg font-medium">Voice Settings</h3>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable_voice">Enable Voice Output</Label>
              <p className="text-sm text-gray-500">Enable AI responses to be spoken aloud</p>
            </div>
            <Switch
              id="enable_voice"
              checked={settings.enable_voice}
              onCheckedChange={(checked) => setSettings({ ...settings, enable_voice: checked })}
            />
          </div>

          {settings.enable_voice && (
            <>
              <div className="space-y-2">
                <Label htmlFor="elevenlabs_api_key">ElevenLabs API Key</Label>
                <Input
                  id="elevenlabs_api_key"
                  value={settings.elevenlabs_api_key || ''}
                  onChange={(e) => setSettings({ ...settings, elevenlabs_api_key: e.target.value })}
                  placeholder="Enter your ElevenLabs API key"
                  type="password"
                />
                <p className="text-sm text-gray-500">Required for voice output</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="voice_id">Voice ID</Label>
                <Input
                  id="voice_id"
                  value={settings.voice_id || ''}
                  onChange={(e) => setSettings({ ...settings, voice_id: e.target.value })}
                  placeholder="Enter the voice ID from ElevenLabs"
                />
                <p className="text-sm text-gray-500">The ID of the voice to use for responses</p>
              </div>
            </>
          )}
        </div>

        <div className="pt-4">
          <Button onClick={handleSaveSettings} disabled={isSaving} className="w-full">
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatConfig;
